import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Button,
  Avatar, Chip, CircularProgress, Alert, Divider, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HomeIcon from '@mui/icons-material/Home';
import PlaceIcon from '@mui/icons-material/Place';
import StorefrontIcon from '@mui/icons-material/Storefront';
import restaurantService from '../../services/restaurant.service';
import realtimeService from '../../services/realtime.service';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
function loadGMaps(cb) {
  if (window.google?.maps) { cb(); return; }
  if (document.querySelector('script[data-gmaps]')) {
    const wait = setInterval(() => { if (window.google?.maps) { clearInterval(wait); cb(); } }, 100);
    return;
  }
  const s = document.createElement('script');
  s.src = `https://maps.googleapis.com/maps/api/js?v=3.54&key=${GOOGLE_KEY}&libraries=places&language=fr`;
  s.async = true; s.defer = true; s.setAttribute('data-gmaps', '1');
  s.onload = cb;
  document.head.appendChild(s);
}

const statusSteps = [
  { key: 'confirmed', label: 'Confirmée', icon: <CheckCircleIcon /> },
  { key: 'preparing', label: 'En préparation', icon: <RestaurantIcon /> },
  { key: 'ready', label: 'Prête', icon: <CheckCircleIcon /> },
  { key: 'picked_up', label: 'Récupérée', icon: <TwoWheelerIcon /> },
  { key: 'delivering', label: 'En livraison', icon: <LocalShippingIcon /> },
  { key: 'delivered', label: 'Livrée', icon: <HomeIcon /> },
];

function createDeliveryMarkerSVG() {
  return '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44"><circle cx="22" cy="22" r="20" fill="#552583" stroke="white" stroke-width="3"/><path d="M27 15c0-1.1-.9-2-2-2h-3v2h3v2.65L21.52 22H18V17H14c-2.21 0-4 1.79-4 4v3h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4.48L27 18.35V15zM15 25c-.55 0-1-.45-1-1h2c0 .55-.45 1-1 1z" fill="white"/></svg>';
}

const OrderTracking = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deliveryPosition, setDeliveryPosition] = useState(null);
  const [eta, setEta] = useState(null);
  const [distance, setDistance] = useState(null);
  const [deliveryCode, setDeliveryCode] = useState(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const pathRef = useRef([]);

  const loadOrder = useCallback(async () => {
    try {
      const data = await restaurantService.getOrder(id);
      setOrder(data.order || data);
    } catch (err) {
      setError('Impossible de charger la commande');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
    realtimeService.connect();
    realtimeService.joinOrder(id);
    realtimeService.requestPosition(id);

    realtimeService.onPositionUpdate((data) => {
      if (String(data.orderId) === String(id)) {
        setDeliveryPosition({ lat: data.latitude, lng: data.longitude, heading: data.heading, speed: data.speed });
      }
    });
    realtimeService.onStatusChange((data) => {
      if (String(data.orderId) === String(id)) setOrder(prev => prev ? { ...prev, order_status: data.status } : prev);
    });
    realtimeService.onEtaChange((data) => {
      if (String(data.orderId) === String(id)) { setEta(data.eta); setDistance(data.distance); }
    });
    realtimeService.onDeliveryCodeAssigned((data) => {
      if (String(data.orderId) === String(id)) setDeliveryCode(data.deliveryCode);
    });

    return () => {
      realtimeService.leaveOrder(id);
      realtimeService.removeAllOrderListeners();
    };
  }, [id, loadOrder]);

  // Initialiser Google Maps (une seule fois quand order est chargé)
  const mapInitDone = useRef(false);
  const polylineRef = useRef(null);

  const initMapCallback = useCallback((node) => {
    if (!node || mapInitDone.current || !order) return;
    mapInitDone.current = true;

    const centerLat = order.delivery_lat || order.restaurant_lat || -12.7806;
    const centerLng = order.delivery_lng || order.restaurant_lng || 45.2278;

    loadGMaps(() => {
      if (!node) return;
      const map = new window.google.maps.Map(node, {
        center: { lat: centerLat, lng: centerLng },
        zoom: 14,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      });
      mapInstanceRef.current = map;

      // Polyline pour le trajet livreur
      polylineRef.current = new window.google.maps.Polyline({
        path: [],
        strokeColor: '#FDB927',
        strokeWeight: 4,
        strokeOpacity: 0.8,
        map,
      });

      const infoWindow = new window.google.maps.InfoWindow();

      // Marqueur restaurant (violet)
      if (order.restaurant_lat && order.restaurant_lng) {
        const restMarker = new window.google.maps.Marker({
          position: { lat: order.restaurant_lat, lng: order.restaurant_lng },
          map,
          title: order.restaurant_name || 'Restaurant',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
              '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36"><circle cx="18" cy="18" r="16" fill="#552583" stroke="white" stroke-width="2"/><text x="18" y="23" text-anchor="middle" fill="white" font-size="18">🍽</text></svg>'
            ),
            scaledSize: new window.google.maps.Size(36, 36),
            anchor: new window.google.maps.Point(18, 18),
          },
        });
        restMarker.addListener('click', () => {
          infoWindow.setContent(order.restaurant_name || 'Restaurant');
          infoWindow.open(map, restMarker);
        });
      }

      // Marqueur destination (or)
      if (order.delivery_lat && order.delivery_lng) {
        const destMarker = new window.google.maps.Marker({
          position: { lat: order.delivery_lat, lng: order.delivery_lng },
          map,
          title: order.delivery_address || 'Destination',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
              '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36"><circle cx="18" cy="18" r="16" fill="#FDB927" stroke="white" stroke-width="2"/><text x="18" y="23" text-anchor="middle" fill="#333" font-size="18">📍</text></svg>'
            ),
            scaledSize: new window.google.maps.Size(36, 36),
            anchor: new window.google.maps.Point(18, 18),
          },
        });
        destMarker.addListener('click', () => {
          infoWindow.setContent(order.delivery_address || 'Destination');
          infoWindow.open(map, destMarker);
        });
      }

      // Fit bounds
      if (order.restaurant_lat && order.restaurant_lng && order.delivery_lat && order.delivery_lng) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: order.restaurant_lat, lng: order.restaurant_lng });
        bounds.extend({ lat: order.delivery_lat, lng: order.delivery_lng });
        map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // Ref callback pour attacher la carte quand le div est monté
  const mapRef = useRef(null);
  const setMapRef = useCallback((node) => {
    mapRef.current = node;
    if (node && order && !mapInitDone.current) initMapCallback(node);
  }, [initMapCallback, order]);

  // Re-init si order arrive après le montage du div
  useEffect(() => {
    if (!loading && order && mapRef.current && !mapInitDone.current) {
      initMapCallback(mapRef.current);
    }
  }, [loading, order, initMapCallback]);

  // Mettre à jour le marqueur du livreur
  useEffect(() => {
    if (!mapInstanceRef.current || !deliveryPosition || !window.google?.maps) return;

    const pos = { lat: deliveryPosition.lat, lng: deliveryPosition.lng };

    if (markerRef.current) {
      markerRef.current.setPosition(pos);
    } else {
      markerRef.current = new window.google.maps.Marker({
        position: pos,
        map: mapInstanceRef.current,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(createDeliveryMarkerSVG()),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        },
      });
    }

    if (polylineRef.current) {
      const path = polylineRef.current.getPath();
      path.push(new window.google.maps.LatLng(pos.lat, pos.lng));
    }

    mapInstanceRef.current.panTo(pos);
  }, [deliveryPosition]);

  const getCurrentStepIndex = () => {
    if (!order) return -1;
    return statusSteps.findIndex(s => s.key === order.order_status);
  };

  const getEtaText = () => {
    if (eta) return `${eta} min`;
    const status = order?.order_status;
    if (status === 'confirmed') return '25-35 min';
    if (status === 'preparing') return '20-30 min';
    if (status === 'ready') return '15-20 min';
    if (status === 'picked_up' || status === 'delivering') return '5-15 min';
    if (status === 'delivered') return 'Livrée !';
    return '--';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress sx={{ color: '#552583' }} />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Commande introuvable'}</Alert>
        <Button component={Link} to="/restaurant/orders" sx={{ mt: 2 }}>Retour</Button>
      </Container>
    );
  }

  const currentStep = getCurrentStepIndex();
  const isDelivering = ['picked_up', 'delivering'].includes(order.order_status);
  const isDelivered = order.order_status === 'delivered';

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <IconButton component={Link} to="/restaurant/orders" sx={{ color: '#552583' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700}>Suivi de commande</Typography>
          <Typography variant="caption" sx={{ color: '#4a4a4a' }}>{order.order_number}</Typography>
        </Box>
      </Box>

      {/* Carte Google Maps */}
      <Box sx={{ position: 'relative', height: { xs: 300, md: 400 } }}>
        <Box ref={setMapRef} sx={{ width: '100%', height: '100%' }} />

        {/* ETA Overlay */}
        <Box sx={{
          position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(240,235,224,0.3)', borderRadius: 3,
          px: 3, py: 1.5, textAlign: 'center', minWidth: 180,
        }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
            {isDelivered ? 'Commande' : 'Arrivée estimée'}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#F0EBE0', lineHeight: 1.2 }}>
            {getEtaText()}
          </Typography>
          {distance && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{distance} km restants</Typography>
          )}
        </Box>

        {/* Indicateur position live */}
        {isDelivering && deliveryPosition && (
          <Box sx={{
            position: 'absolute', bottom: 16, right: 16, zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(0,0,0,0.8)', borderRadius: 2, px: 2, py: 0.8,
          }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%', bgcolor: '#F0EBE0',
              animation: 'pulse-glow 1.5s ease-in-out infinite',
              '@keyframes pulse-glow': {
                '0%, 100%': { boxShadow: '0 0 4px rgba(240,235,224,0.5)' },
                '50%': { boxShadow: '0 0 12px rgba(240,235,224,0.8)' },
              },
            }} />
            <Typography variant="caption" sx={{ color: '#F0EBE0', fontWeight: 600 }}>Position en direct</Typography>
          </Box>
        )}

        {!deliveryPosition && isDelivering && (
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ color: '#F0EBE0', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'white' }}>Connexion au livreur...</Typography>
          </Box>
        )}
      </Box>

      <Container maxWidth="md">
        {/* Timeline statut */}
        <Card sx={{ mt: -4, position: 'relative', zIndex: 10, mx: { xs: 2, md: 0 } }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <AccessTimeIcon sx={{ color: '#552583' }} />
              <Typography variant="h6" fontWeight={700}>Statut de la commande</Typography>
            </Box>
            <Box sx={{ position: 'relative', pl: 3 }}>
              {statusSteps.map((step, index) => {
                const isCompleted = index <= currentStep;
                const isCurrent = index === currentStep;
                return (
                  <Box key={step.key} sx={{ display: 'flex', alignItems: 'flex-start', mb: index < statusSteps.length - 1 ? 3 : 0, position: 'relative' }}>
                    {index < statusSteps.length - 1 && (
                      <Box sx={{ position: 'absolute', left: -16, top: 28, width: 2, height: 'calc(100% + 12px)', bgcolor: isCompleted ? '#552583' : 'rgba(0,0,0,0.1)', transition: 'background-color 0.5s' }} />
                    )}
                    <Box sx={{
                      position: 'absolute', left: -24, top: 2, width: 18, height: 18, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: isCompleted ? '#552583' : 'rgba(0,0,0,0.06)',
                      border: isCurrent ? '3px solid #552583' : 'none',
                      boxShadow: isCurrent ? '0 0 15px rgba(85,37,131,0.4)' : 'none',
                      transition: 'all 0.5s',
                    }}>
                      {isCompleted ? <CheckCircleIcon sx={{ fontSize: 14, color: 'white' }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 10, color: 'rgba(0,0,0,0.25)' }} />}
                    </Box>
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body1" fontWeight={isCurrent ? 700 : 400} sx={{ color: isCompleted ? '#1a1a1a' : '#7a7a7a' }}>
                        {step.label}
                      </Typography>
                      {isCurrent && !isDelivered && (
                        <Chip label="En cours" size="small" sx={{ mt: 0.5, bgcolor: 'rgba(85,37,131,0.1)', color: '#552583', fontWeight: 600, border: '1px solid rgba(85,37,131,0.25)', animation: 'pulse-glow 2s ease-in-out infinite' }} />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>

        {/* Adresses : restaurant + destination */}
        {(order.restaurant_address || order.delivery_address) && (
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="overline" sx={{ color: '#552583', fontWeight: 600, letterSpacing: 2 }}>ITINÉRAIRE</Typography>
              <Box mt={2}>
                {/* Restaurant */}
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(85,37,131,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <StorefrontIcon sx={{ color: '#552583', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#7a7a7a', fontSize: '0.75rem' }}>Recuperation</Typography>
                    <Typography variant="body1" fontWeight={600}>{order.restaurant_name}</Typography>
                    <Typography variant="body2" sx={{ color: '#4a4a4a' }}>{order.restaurant_address}</Typography>
                  </Box>
                </Box>

                {/* Ligne pointillee */}
                {order.delivery_address && (
                  <Box sx={{ ml: '19px', borderLeft: '2px dashed rgba(85,37,131,0.3)', height: 20, mb: 1 }} />
                )}

                {/* Destination */}
                {order.delivery_address && (
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(253,185,39,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PlaceIcon sx={{ color: '#FDB927', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#7a7a7a', fontSize: '0.75rem' }}>Livraison</Typography>
                      <Typography variant="body1" fontWeight={600}>{order.delivery_address}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Code de livraison */}
        {(deliveryCode || (isDelivering && order.delivery_code)) && !isDelivered && (
          <Card sx={{ mt: 3, border: '2px solid rgba(85,37,131,0.25)', background: 'linear-gradient(135deg, rgba(85,37,131,0.08), rgba(85,37,131,0.15))' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="overline" sx={{ color: '#552583', fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem' }}>CODE DE LIVRAISON</Typography>
              <Typography variant="h2" fontWeight={900} sx={{ color: '#552583', letterSpacing: '0.3em', my: 1.5, fontFamily: 'monospace', textShadow: '0 0 30px rgba(85,37,131,0.2)' }}>
                {deliveryCode || order.delivery_code}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4a4a4a', maxWidth: 280, mx: 'auto', lineHeight: 1.6 }}>
                Communiquez ce code à votre livreur lorsqu'il arrive.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Infos livreur */}
        {order.delivery_person_id && (isDelivering || isDelivered) && (
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="overline" sx={{ color: '#552583', fontWeight: 600, letterSpacing: 2 }}>VOTRE LIVREUR</Typography>
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(85,37,131,0.1)', color: '#552583', fontSize: '1.5rem', fontWeight: 700, border: '2px solid rgba(85,37,131,0.25)' }}>
                  {order.delivery_person_name?.[0] || 'L'}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={600}>{order.delivery_person_name || 'Livreur Usafiri'}</Typography>
                  <Typography variant="body2" sx={{ color: '#4a4a4a' }}>
                    {deliveryPosition ? `Vitesse: ${(deliveryPosition.speed * 3.6).toFixed(0)} km/h` : 'En attente de position...'}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton sx={{ bgcolor: 'rgba(85,37,131,0.1)', color: '#552583', '&:hover': { bgcolor: 'rgba(85,37,131,0.2)' } }} href={order.delivery_person_phone ? `tel:${order.delivery_person_phone}` : '#'}>
                    <PhoneIcon />
                  </IconButton>
                  <IconButton sx={{ bgcolor: 'rgba(0,0,0,0.04)', color: '#4a4a4a', '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } }}>
                    <ChatIcon />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Résumé commande */}
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" sx={{ color: '#552583', fontWeight: 600, letterSpacing: 2 }}>DÉTAILS DE LA COMMANDE</Typography>
            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" sx={{ color: '#4a4a4a' }}>Restaurant</Typography>
                <Typography variant="body1" fontWeight={600}>{order.restaurant_name || 'Restaurant'}</Typography>
              </Box>
              <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)', my: 1 }} />
              {order.items && order.items.map((item, i) => (
                <Box key={i} display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" sx={{ color: '#1a1a1a' }}>{item.quantity}x {item.name || item.menu_item_name}</Typography>
                  <Typography variant="body2" fontWeight={500}>{item.total_price || item.quantity * item.unit_price} €</Typography>
                </Box>
              ))}
              <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)', my: 1.5 }} />
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ color: '#4a4a4a' }}>Sous-total</Typography>
                <Typography variant="body2">{order.subtotal} €</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ color: '#4a4a4a' }}>Livraison</Typography>
                <Typography variant="body2">{order.delivery_fee === 0 ? 'Gratuit' : `${order.delivery_fee} €`}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ color: '#4a4a4a' }}>Taxes</Typography>
                <Typography variant="body2">{order.tax} €</Typography>
              </Box>
              <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)', my: 1.5 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight={700}>Total</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#552583' }}>{order.total_amount} €</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default OrderTracking;
